<script setup lang="ts">
import { getRepairRequest } from '@/services/rc.service'
import { getAllWorkOrderShipments } from '@/services/rc/work-orders.service'
import { Icon } from '@iconify/vue'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const props = defineProps<{ workOrder?: any }>()
const emit = defineEmits(['drawerOpened', 'drawerClosed', 'repairRequestLinkClick'])

// refs
const drawerOpen = ref<boolean>(false)
const queryEnabled = computed(() => {
  return drawerOpen.value && props.workOrder?.repair_request_number !== null
})

// Watch for new selected work order from parent (work orders view).
watch(
  () => props.workOrder,
  (newValue) => (drawerOpen.value = newValue ? true : false)
)

const { isFetching: isFetchingRepairRequest, data: repairRequestData } = useQuery({
  queryKey: ['repair-request-details', props.workOrder?.repair_request_number],
  queryFn: () => getRepairRequest(props.workOrder?.repair_request_number),
  enabled: queryEnabled,
})

const { isFetching: isFetchingShipments, data: shipmentData } = useQuery({
  queryKey: ['work-order-shipments', props.workOrder?.number],
  queryFn: () => getAllWorkOrderShipments(props.workOrder?.number),
  enabled: queryEnabled,
})

const navigateToWorkOrderDashboard = async (number: string) => {
  router.push({ name: 'rc-work-order-dashboard', params: { number } })
}

const navigateToShipmentDetails = async (shipmentId: number) => {
  router.push({ name: 'rc-logistics-shipment-details', params: { shipmentId: shipmentId } })
}
</script>

<template>
  <el-drawer
    v-model="drawerOpen"
    :size="384"
    :show-close="false"
    @opened="emit('drawerOpened')"
    @closed="emit('drawerClosed')"
  >
    <template v-if="workOrder">
      <div class="drawer-content">
        <header class="drawer-content__header">
          <div class="drawer-content__toolbar">
            <div class="drawer-content__title">
              <span class="rl-label">Work Order #</span>
              <CopyButton :value="workOrder.number" :hide-message="true">
                <h3>#{{ workOrder.number.slice(-4) }}</h3>
              </CopyButton>
            </div>
            <div>
              <el-button
                class="close-button"
                style="height: 28px; width: 28px"
                circle
                @click="() => emit('drawerClosed')"
                ><Icon icon="material-symbols:close"
              /></el-button>
            </div>
          </div>
          <section class="drawer-content__section inline">
            <div class="drawer-content__detail-item">
              <span class="rl-label">Status</span>
              <div>
                <StatusBadge :status="workOrder.status" />
              </div>
            </div>
            <div class="drawer-content__detail-item">
              <span class="rl-label">Date started</span>
              <span>{{ new Date(workOrder.start_date)?.toLocaleDateString() }}</span>
            </div>
          </section>
        </header>
        <template v-if="queryEnabled">
          <el-collapse v-if="isFetchingRepairRequest || repairRequestData" v-loading="isFetchingRepairRequest">
            <el-collapse-item>
              <template #title>
                <header class="collapse-item-header">Details</header>
              </template>
              <section v-if="repairRequestData" class="drawer-content__section">
                <div class="drawer-content__detail-item">
                  <span class="rl-label"># of Miners</span>
                  <span>{{ workOrder.expected_units }}</span>
                </div>
                <div class="drawer-content__detail-item">
                  <span class="rl-label">Scope of work</span>
                  <rc-scope-of-work :scope-of-work="repairRequestData.scope_of_work" />
                </div>
              </section>
              <rl-link-item
                v-if="repairRequestData"
                :label="`Repair Request #${repairRequestData.number.slice(-4)}`"
                @click="emit('repairRequestLinkClick', repairRequestData.number)"
              />
            </el-collapse-item>
            <el-collapse-item v-if="isFetchingShipments || shipmentData" v-loading="isFetchingShipments">
              <template #title>
                <header class="collapse-item-header">Shipments</header>
              </template>
              <div v-for="shipment in shipmentData" :key="shipment.id" class="shipment-container">
                <RlWorkOrderShipmentLinkItem
                  :shipment="shipment"
                  @click="navigateToShipmentDetails(shipment.id)"
                ></RlWorkOrderShipmentLinkItem>
              </div>
            </el-collapse-item>
          </el-collapse>
          <el-alert v-else title="Error" type="error">Failed to fetch repair request details.</el-alert>
          <el-button type="primary" @click="navigateToWorkOrderDashboard(workOrder.number)">Dashboard</el-button>
        </template>
      </div>
    </template></el-drawer
  >
</template>

<style scoped lang="scss">
.shipment-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-xs);
  padding-bottom: var(--spacing-spacing-xs);
}

.drawer-content {
  display: flex;
  flex-direction: column;

  &__header {
    display: inherit;
    flex-direction: inherit;
    gap: var(--spacing-spacing-xl);
  }

  &__toolbar {
    display: inline-flex;
    gap: var(--spacing-spacing-md);
  }

  &__title {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    gap: var(--spacing-spacing-xs);

    h3 {
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      color: var(--text-primary);
    }
  }

  &__section {
    display: flex;
    flex-direction: column;
    padding-top: var(--spacing-spacing-lg);
    padding-bottom: var(--spacing-spacing-lg);
    gap: var(--spacing-spacing-lg);
  }

  &__detail-item {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--spacing-spacing-xs);
    color: var(--text-primary);
    font-size: var(--font-size-body-compact);
    font-weight: 500;
    line-height: 20px;
  }

  .inline {
    flex-direction: row;
  }

  .collapse-item-header {
    width: 100%;
    display: inline-flex;
  }
}

::v-deep(.el-collapse-item__header) {
  flex-direction: row-reverse;
}
</style>
